import useAudioStore from '../../../../shared/store/audio.store.tsx'
import { AudioFormService } from '../../data/audio-form.service.ts'
import type { AudioFormInput } from '../../data/dto/audio-form-input.ts'
import type { AudioFormResultResponseDto } from '../../data/dto/audio-form-result.dto.ts'
import { stripBaseApiUrl } from '@/utils/strip-base-api-url.ts'
import type { AudioForm } from '../audio-form.ts'
import { WorkflowId } from '@/models'

const wait = async (ms: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

const isInProgress = (
  payload: AudioFormResultResponseDto
): payload is {
  message: string
  audioform_id?: string
  audioformId?: string
} => 'message' in payload && !('data' in payload)

export const fetchAudioFormUntilProcessed = async (
  audioFormId: string,
  maxRetries = 90
): Promise<AudioFormInput> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const response = await AudioFormService.getAudioFormVersion(audioFormId)

    if (isInProgress(response)) {
      await wait(1000)
      continue
    }

    const { statusCode, result } = response.data
    if (statusCode === 202) {
      await wait(1000)
      continue
    }
    if (statusCode !== 200) {
      throw new Error(response.data.message ?? response.message)
    }
    if (result) return result

    throw new Error('AudioForm build returned 200 without a result')
  }
  throw new Error('AudioForm still processing after max retries')
}

export const generateAudioform = async (
  audioFormInput: AudioFormInput,
  sessionId: string
): Promise<AudioForm> => {
  const internalData = {
    workflowName: WorkflowId.PRO_EDITOR,
    sessionId,
    moduleName: WorkflowId.PRO_EDITOR,
  }

  const audioFormId = await AudioFormService.createAudioFormVersion(
    audioFormInput,
    internalData
  )
  const audioForm = await fetchAudioFormUntilProcessed(audioFormId)
  const url = audioForm.delivery.uri
  if (!url) {
    throw new Error('AudioForm delivery URI is missing')
  }
  const arrayBuffer = await AudioFormService.getArrayBuffer(
    stripBaseApiUrl(url)
  )

  const audioContext = useAudioStore.getState().audioContext
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
  return {
    id: audioFormId,
    url,
    audioBuffer,
  }
}
